import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { helocBadgeIcon, homeEquityBadgeIcon, mortgageBadgeIcon } from '@ld.consumer-portal/data-uri';
import type { Loan } from '@ld.consumer-portal/models';
import { SvgIconComponent } from '@ld.consumer-portal/ui-shared';
import { LoanSelectionService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export type LoanTypeDisplay = 'MORTGAGE' | 'HELOC' | 'HOME_EQUITY';

@Component({
  selector: 'ld-loan-selection-modal',
  imports: [BaseModalComponent, SvgIconComponent, TranslocoDirective],
  templateUrl: './loan-selection-modal.component.html',
  styleUrl: './loan-selection-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanSelectionModalComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly loanSelectionService = inject(LoanSelectionService);

  loans = input.required<Loan[]>();

  title = input<string>(this.transloco.translate('commonLabelsSelectLoan'));

  subtitle = input<string>(this.transloco.translate('commonLabelsSelectLoanSubtitle'));

  selectedLoanNumber = input<string | null>(null);

  loanSelected = output<Loan>();

  cancel = output<void>();

  protected readonly mortgageIcon = mortgageBadgeIcon;
  protected readonly helocIcon = helocBadgeIcon;
  protected readonly homeEquityIcon = homeEquityBadgeIcon;

  protected getLoanTypeShortName(loan: Loan): LoanTypeDisplay {
    return this.loanSelectionService.getLoanTypeShortName(loan);
  }

  protected getLoanIcon(loan: Loan): string {
    const loanType = this.getLoanTypeShortName(loan);
    if (loanType === 'HELOC') return this.helocIcon;
    if (loanType === 'HOME_EQUITY') return this.homeEquityIcon;
    return this.mortgageIcon;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected getLoanTypeColorClass(loan: Loan): string {
    return 'text-ld-primary';
  }

  protected getAccountDisplay(loan: Loan): string {
    const loanNumber = loan.loanNumber || '';
    const last4 = loanNumber.slice(-4);
    return this.transloco.translate('commonAccountNumber', { last4 });
  }

  protected getAddressDisplay(loan: Loan): string {
    const property = loan.property;
    if (!property) {
      return '';
    }
    const parts = [
      property.address,
      property.city,
      property.state ? `${property.state} ${property.zip || ''}`.trim() : property.zip,
    ].filter(Boolean);
    return parts.join(', ');
  }

  protected isSelected(loan: Loan): boolean {
    return this.selectedLoanNumber() === loan.loanNumber;
  }

  protected onLoanSelect(loan: Loan): void {
    this.loanSelected.emit(loan);
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.cancel.emit();
    }
  }
}
